import { Injectable, Logger } from '@nestjs/common';
import { createHmac, timingSafeEqual } from 'node:crypto';

/**
 * Signed, expiring links to a single invoice.
 *
 * A mailed link cannot carry a bearer token, so this is the one place invoices
 * are reachable without signing in — which is a real trade-off, made
 * deliberately:
 *
 *   - The link is forwardable. So is the PDF itself, which is what the
 *     alternative (attaching it) puts in the same inbox. The email already
 *     contains the customer's name and order.
 *   - It grants exactly one invoice, never a listing, never another customer's.
 *   - It expires. The signed-in route does not.
 *   - The token carries no personal data — an id and an expiry, nothing that
 *     leaks by being logged.
 *
 * The signed-in endpoints stay as they are: no signed URLs there, because a
 * session can answer "who is this" on every request and a link cannot.
 */
@Injectable()
export class InvoiceLinkService {
  private readonly logger = new Logger(InvoiceLinkService.name);

  /** Base for the emailed URL. Follows the same env the cart reminder uses. */
  private get origin(): string {
    return (
      process.env.INVOICE_LINK_BASE_URL ??
      process.env.DEFAULT_FRONTEND_ORIGIN ??
      'http://localhost:4200'
    ).replace(/\/+$/, '');
  }

  /**
   * How long an emailed link stays valid.
   *
   * Long enough that a customer who opens the mail a fortnight later still gets
   * their invoice; short enough that a link forwarded or left in a mailbox
   * backup stops working. After it expires the invoice is still there — they
   * sign in and download it, and that path never expires.
   *
   * A getter, like the other two: read once at module load it could not be
   * changed after boot, and could not be tested at all.
   */
  private get ttlDays(): number {
    const configured = Number(process.env.INVOICE_LINK_TTL_DAYS);
    return Number.isFinite(configured) && configured >= 0 ? configured : 30;
  }

  private get secret(): string | null {
    // Falls back to the auth secret rather than to a hard-coded default: a
    // predictable signing key here would make every invoice public.
    return process.env.INVOICE_LINK_SECRET ?? process.env.AUTH_JWT_SECRET ?? null;
  }

  /** The full URL to put in an email, or null if signing is not configured. */
  urlFor(invoiceId: string): string | null {
    const token = this.sign(invoiceId);
    return token ? `${this.origin}/api/invoices/d/${token}` : null;
  }

  sign(invoiceId: string): string | null {
    const secret = this.secret;
    if (!secret) {
      this.logger.error(
        'no INVOICE_LINK_SECRET or AUTH_JWT_SECRET — invoice links cannot be signed'
      );
      return null;
    }

    const expires = Date.now() + this.ttlDays * 24 * 60 * 60 * 1000;
    const payload = `${invoiceId}.${expires}`;

    return `${base64url(payload)}.${this.mac(payload, secret)}`;
  }

  /**
   * The invoice a token refers to, or null if it is forged, tampered with or
   * expired. Never throws and never says which of those it was — a caller that
   * distinguishes them hands an attacker a way to probe.
   */
  verify(token: string): string | null {
    const secret = this.secret;
    if (!secret) return null;

    const [encoded, signature] = token.split('.');
    if (!encoded || !signature) return null;

    let payload: string;
    try {
      payload = Buffer.from(encoded, 'base64url').toString('utf8');
    } catch {
      return null;
    }

    const expected = this.mac(payload, secret);

    // Constant-time: a plain === leaks how much of the signature was right.
    const a = Buffer.from(signature);
    const b = Buffer.from(expected);
    if (a.length !== b.length || !timingSafeEqual(a, b)) return null;

    const separator = payload.lastIndexOf('.');
    if (separator < 1) return null;

    const invoiceId = payload.slice(0, separator);
    const expires = Number(payload.slice(separator + 1));

    if (!invoiceId || !Number.isFinite(expires) || Date.now() > expires) return null;

    return invoiceId;
  }

  private mac(payload: string, secret: string): string {
    return createHmac('sha256', secret).update(payload).digest('base64url');
  }
}

function base64url(value: string): string {
  return Buffer.from(value, 'utf8').toString('base64url');
}
